import React, { createContext, useContext, useEffect, useState } from "react";
import { fbEvent } from "@/lib/pixel";

const CartContext = createContext(null);
const KEY = "adiyogi_cart";
const WISH_KEY = "adiyogi_wishlist";

// Line ID = the stable identity of a cart line.
// Includes variant_id so Small vs Medium of the same product stay separate.
const buildLineId = (product_id, variant_id, size, design) =>
  `${product_id}::${variant_id || ""}::${size || ""}::${design || ""}`;

// Legacy carts (saved before variant_id / line_id were introduced) may be
// missing line_id — backfill on load so update/remove always match.
const normalize = (raw) => {
  if (!Array.isArray(raw)) return [];
  return raw
    .filter((it) => it && it.product_id)
    .map((it) => ({
      ...it,
      quantity: Math.max(1, parseInt(it.quantity) || 1),
      variant_id: it.variant_id || null,
      size: it.size || null,
      design: it.design || null,
      line_id: it.line_id || buildLineId(it.product_id, it.variant_id, it.size, it.design),
    }));
};

export const CartProvider = ({ children }) => {
  const [items, setItems] = useState(() => {
    try { return normalize(JSON.parse(localStorage.getItem(KEY) || "[]")); } catch { return []; }
  });
  const [wishlist, setWishlist] = useState(() => {
    try { return JSON.parse(localStorage.getItem(WISH_KEY) || "[]"); } catch { return []; }
  });
  const [hydrated, setHydrated] = useState(false);

  useEffect(() => { setHydrated(true); }, []);

  useEffect(() => {
    if (!hydrated) return;
    localStorage.setItem(KEY, JSON.stringify(items));
  }, [items, hydrated]);

  useEffect(() => {
    if (!hydrated) return;
    localStorage.setItem(WISH_KEY, JSON.stringify(wishlist));
  }, [wishlist, hydrated]);

  const addToCart = (product, qty = 1, size = null, design = null, variant_id = null) => {
    const q = Math.max(1, parseInt(qty) || 1);
    fbEvent("AddToCart", {
      content_ids: [product.sku || product.product_id],
      content_name: product.name,
      content_type: "product",
      value: (Number(product.price) || 0) * q,
      currency: "INR",
    });
    const line_id = buildLineId(product.product_id, variant_id, size, design);
    setItems((prev) => {
      const idx = prev.findIndex((p) => p.line_id === line_id);
      if (idx >= 0) {
        // Same product+variant+size+design → increment quantity (never duplicate)
        const next = prev.slice();
        next[idx] = { ...next[idx], quantity: next[idx].quantity + q };
        return next;
      }
      return [
        ...prev,
        {
          line_id,
          product_id: product.product_id,
          variant_id: variant_id || null,
          size: size || null,
          design: design || null,
          name: product.name,
          slug: product.slug,
          image: product.image || (product.images || [])[0],
          price: product.price,
          mrp: product.mrp,
          unit_label: product.unit_label || "",
          dimensions: product.dimensions || "",
          quantity: q,
        },
      ];
    });
  };

  const updateQty = (line_id, quantity) => {
    const q = parseInt(quantity);
    if (!Number.isFinite(q) || q <= 0) return removeFromCart(line_id);
    setItems((prev) => prev.map((p) => (p.line_id === line_id ? { ...p, quantity: q } : p)));
  };

  const removeFromCart = (line_id) => {
    setItems((prev) => prev.filter((p) => p.line_id !== line_id));
  };

  const clearCart = () => setItems([]);

  const toggleWishlist = (product) => {
    setWishlist((prev) => {
      const exists = prev.find((p) => p.product_id === product.product_id);
      if (exists) return prev.filter((p) => p.product_id !== product.product_id);
      return [
        ...prev,
        {
          product_id: product.product_id,
          name: product.name,
          slug: product.slug,
          image: (product.images || [])[0],
          price: product.price,
          mrp: product.mrp,
        },
      ];
    });
  };

  const isWishlisted = (product_id) => wishlist.some((p) => p.product_id === product_id);

  const subtotal = items.reduce((s, it) => s + (Number(it.price) || 0) * it.quantity, 0);
  const itemCount = items.reduce((s, it) => s + it.quantity, 0);

  return (
    <CartContext.Provider
      value={{ items, subtotal, itemCount, addToCart, updateQty, removeFromCart, clearCart, wishlist, toggleWishlist, isWishlisted }}
    >
      {children}
    </CartContext.Provider>
  );
};

export const useCart = () => useContext(CartContext);
